import { useState } from 'react';
import { X, Share2, Compass, Shield } from 'lucide-react';
import { setOnboardingCompleted } from '../lib/onboarding';

interface OnboardingWalkthroughProps {
  onComplete: () => void;
}

const STEPS = [
  {
    title: 'Welcome to USA Visible',
    body: 'Discover and share viral content across YouTube, Instagram, X, and SoundCloud — in one free-speech community feed.',
    icon: Compass,
  },
  {
    title: 'Share what matters',
    body: 'Tap Share, paste a public link, and it joins the real-time feed for everyone to explore.',
    icon: Share2,
  },
  {
    title: 'Browse freely',
    body: 'Use the platform pages to filter by network. Your privacy matters — we only store public media links you choose to share.',
    icon: Shield,
  },
];

export default function OnboardingWalkthrough({ onComplete }: OnboardingWalkthroughProps) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const finish = () => {
    setOnboardingCompleted();
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        className="relative w-full max-w-md rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl p-6 sm:p-8"
      >
        <button
          type="button"
          onClick={finish}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label="Skip onboarding"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center gap-4 pt-2">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-pink-500 to-orange-400 flex items-center justify-center text-white shadow-lg">
            <Icon size={32} />
          </div>
          <h2 id="onboarding-title" className="text-2xl font-bold text-slate-900 dark:text-white">
            {current.title}
          </h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{current.body}</p>

          <div className="flex gap-2 mt-2" aria-hidden>
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-8 rounded-full transition-colors ${
                  i === step ? 'bg-pink-500' : 'bg-slate-200 dark:bg-slate-700'
                }`}
              />
            ))}
          </div>

          <div className="flex w-full gap-3 mt-4">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
              className="flex-1 py-3 rounded-xl bg-pink-600 hover:bg-pink-500 text-white font-semibold shadow-md"
            >
              {isLast ? 'Get started' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
